// Shared cross-route state for the Ros shell. AppShell owns ALL app state (it is the former RosApp,
// reorganized); this context exposes ONLY the thin slice the rail + Home + Chats pages need. The
// chat/panel state (mcpPanelData, siteArtifacts, draft/version, drag, publishing, …) stays private to
// AppShell/ChatWorkspace and is NEVER put here — so chat streaming never re-renders the rail.
//
// Note we expose the OPERATION `startNewChatWithMessage`, not `chatRef`: route pages (Home) start a
// chat through a narrow imperative call; they never touch the chat instance directly. R3Chat is
// always-mounted in the shell, so this op is safe to call from any route (chatRef.current is non-null).

import { createContext, useContext } from 'react'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import type { UserSettings, UserSettingsPatch } from './pages/userSettingsApi'
import type { SessionArtifact } from './components/rosArtifacts'

export interface RosShellValue {
  // Sessions (server-paginated) — drives the rail recent list, Home recent-chats, Chats page.
  sessions: SessionSummary[]
  sessionsLoading: boolean
  sessionsHasMore: boolean
  loadMoreSessions: () => void
  /** Re-fetch the first page (after rename/delete on the Chats page). */
  reloadSessions: () => void

  // Active session (React state, NOT URL-derived — see AppShell for why). The URL reflects it.
  liveSessionId: string | undefined
  /** True once the live chat has any messages. Drives the "Ask Ros" rail highlight: a blank fresh
      chat highlights Ask Ros; the moment the conversation has content the highlight drops. */
  chatHasMessages: boolean
  /** Artifact count per session id — drives the "has artifacts" indicator on chat lists (rail, Chats
      page, project Add-chat picker). 0/absent = no artifacts. Best-effort, refreshed globally. */
  artifactCountBySession: Record<string, number>
  /** Resume a session: load its history + restore its panel. Navigates the caller to /chat.
      Pass artifactId to open that specific artifact beside the chat. */
  selectSession: (sessionId: string, artifactId?: string) => void
  /** Open an artifact beside its source chat, or beside a fresh chat when it has no source chat. */
  openArtifactInChat: (artifact: SessionArtifact, sourceSessionId?: string | null) => Promise<void>
  /** Clear to a fresh chat surface (no prior context). */
  newChat: () => void
  /** Start a fresh chat that auto-associates with the given project once its first message creates the
      session. Used by the project detail page's "New chat" button. */
  newChatInProject: (projectId: string) => void
  /** Start a fresh chat AND send the first message (Home "Ask Ros"). */
  startNewChatWithMessage: (text: string) => void
  /**
   * Open a micro-app in the chat right-side panel (Accept gate + Cap'n Web).
   * Navigates to /chat so chat + panel are visible together. Does NOT auto-accept (B1).
   */
  openMicroApp: (definitionId: string, displayName?: string) => void
  /** Rename a session (Chats inline rename). Updates local state optimistically. */
  renameSession: (sessionId: string, name: string) => Promise<void>
  /** Pin or unpin a chat. Persisted server-side and updated optimistically. */
  setSessionPinned: (sessionId: string, pinned: boolean) => Promise<void>
  /** Delete a session (Chats delete). Returns the removed summary so the caller can offer Undo. */
  deleteSession: (sessionId: string) => Promise<void>
  /** Re-create a session row locally + reload (Undo after delete). */
  restoreSessionLocally: (session: SessionSummary) => void

  // Overlays / flags reused by the rail.
  openHistory: () => void
  openAgents: () => void
  showAgents: boolean
  /** Open the Settings overlay (from the sidebar item + the account menu). */
  openSettings: () => void

  // User profile settings (Settings page). null until loaded; drives the rail avatar + Home greeting.
  userSettings: UserSettings | null
  /** True once the settings fetch has resolved — greetings hold the name until then (no flicker). */
  userSettingsLoaded: boolean
  saveUserSettings: (patch: UserSettingsPatch) => Promise<UserSettings>
  uploadUserAvatar: (file: File) => Promise<void>

  // Rail collapse (persisted; auto-collapses on /chat). Exposed so the rail toggle + pages agree.
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void

  // Identity for the account block.
  email: string | null
  name: string | null
  apiBaseUrl: string
  token: string | null
  /** End the Ros session and land on the signed-out screen. */
  logout: () => Promise<void>
}

export const RosShellContext = createContext<RosShellValue | null>(null)

export function useRosShell(): RosShellValue {
  const ctx = useContext(RosShellContext)
  if (!ctx) throw new Error('useRosShell must be used within <RosShellContext.Provider> (AppShell)')
  return ctx
}
